import dayjs from "dayjs";

import {
  ApplicationCalculationDto,
  ApplicationCargoCharacteristicDto,
  ApplicationCargoInfoDto,
  ApplicationFilterDto,
  ApplicationDto,
  ServiceCostApplicationCalculationDto,
  ApplicationMatchDto,
  ApplicationClientInfoDto,
  ApplicationLiteDto,
  ApplicationCalculationVehicleDto,
  ApplicationCalculationSegmentDto,
  WishElementDto,
  ApplicationCommentInfoDto,
} from "../../dto/Applications/ApplicationDto";
import AttachDocumentDto from "../../dto/Documents/AttachDocumentDto";
import { TransportCharacteristicsLoadType } from "../../dto/Settings/TransportCharacteristicDto";
import LocationModel from "../Location/LocationModel";
import { CurrencyModel } from "../Settings/CurrencyModel";
import { TransportCharacteristicModel } from "../Settings/TransportCharacteristicModel";
import i18n from "../../i18n";
import { SupportedLanguages } from "../Settings/TranslationModel";
import { AttachDocumentDataConverter, AttachDocumentFormModel } from "../Documents/AttachDocumentModel";
import { DealDocumentModel } from "../Deal/DealModel";
import RateModel, { RateDataConverter } from "../Rate/RateModel";
import _ from "lodash";
import SegmentModel from "../Segment/SegmentModel";
import VatUtils from "../../utils/VatUtils";
import { BitrixCompanyDataConverter, BitrixCompanyModel } from "../Dictionaries/BitrixCompanyModel";

export type ApplicationLiteModel = Omit<ApplicationLiteDto, "creationDate" | "updateDate" | "bitrixCompanyInfo"> & {
  creationDate: dayjs.Dayjs;
  updateDate?: dayjs.Dayjs;
  bitrixCompanyInfo?: BitrixCompanyModel;
};

export type CargoCharacteristicModel = Omit<ApplicationCargoCharacteristicDto, "id"> & {
  id?: number;
};

export type ApplicationDangerousCargoInfoModel = Omit<NonNullable<ApplicationCargoInfoDto["dangerousCargoInfo"]>, "documents"> & {
  documents?: Array<AttachDocumentFormModel>;
};

export type ApplicationCargoInfoModel = Omit<
  ApplicationCargoInfoDto,
  "cargoCharacteristics" | "dangerousCargoInfo" | "cargoDimensionsDocuments"
> & {
  cargoCharacteristics?: Array<CargoCharacteristicModel>;
  cargoDimensionsDocuments?: Array<AttachDocumentFormModel>;
  dangerousCargoInfo?: ApplicationDangerousCargoInfoModel;
};

export type ApplicationFilterModel = Omit<
  ApplicationFilterDto,
  "from" | "waypoint" | "to" | "date" | "arrivalDateTo" | "currency" | "borderCrossing" | "customsPost"
> & {
  fromLocation?: LocationModel;
  waypointLocation?: LocationModel;
  toLocation?: LocationModel;
  fromDate?: Date;
  toDate?: Date;
  transportCharacteristic: TransportCharacteristicModel;
  currency?: CurrencyModel;
  borderCrossingLocation?: LocationModel;
  customsPostsLocation?: LocationModel;
};

export type ServiceCostApplicationCalculationModel = Omit<ServiceCostApplicationCalculationDto, "id" | "bitrixCompanyInfo"> & {
  id?: number;
  bitrixCompanyInfo?: BitrixCompanyModel;
};

export type WishElementModel = Omit<WishElementDto, "rate" | "bitrixCompanyInfo"> & {
  rate?: RateModel;
  bitrixCompanyInfo: BitrixCompanyModel;
};

export type ApplicationCalculationVehicleModel = Omit<ApplicationCalculationVehicleDto, "wishElementList"> & {
  wishElementList: Array<WishElementModel>;
};

export type ApplicationCalculationSegmentModel = Omit<
  ApplicationCalculationSegmentDto,
  "calculationVehicles" | "recommendedRates" | "segment"
> & {
  calculationVehicles: Array<ApplicationCalculationVehicleModel>;
  recommendedRates: Array<RateModel>;
  segment: SegmentModel;
};

export type ApplicationCalculationModel = Omit<ApplicationCalculationDto, "id" | "serviceCostCalculations" | "calculationSegments"> & {
  id?: number;
  serviceCostCalculations: Array<ServiceCostApplicationCalculationModel>;
  calculationSegments?: Array<ApplicationCalculationSegmentModel>;
};

export type ApplicationDocument = AttachDocumentDto;

export type ApplicationClientInfoModel = Omit<ApplicationClientInfoDto, "id"> & {
  id?: number;
};

export type ApplicationCommentInfoModel = Omit<ApplicationCommentInfoDto, "date"> & {
  date: dayjs.Dayjs;
};

export type ApplicationModel = Omit<
  ApplicationDto,
  | "creationDate"
  | "indicativeCreationDate"
  | "applicationForOfferCreationDate"
  | "offerCreationDate"
  | "lastDateOfChangeToOffer"
  | "updateDate"
  | "calculations"
  | "filter"
  | "cargoInfo"
  | "documents"
  | "clientInfo"
  | "commentInfos"
  | "bitrixCompanyInfo"
> & {
  type: TransportCharacteristicsLoadType;
  creationDate: Date;
  indicativeCreationDate?: Date;
  applicationForOfferCreationDate?: Date;
  offerCreationDate?: Date;
  lastDateOfChangeToOffer?: Date;
  calculations: Array<ApplicationCalculationModel>;
  filter: ApplicationFilterModel;
  cargoInfo?: ApplicationCargoInfoModel;
  documents: Array<AttachDocumentFormModel>;
  clientInfo?: ApplicationClientInfoModel;
  commentInfos: Array<ApplicationCommentInfoModel>;
  bitrixCompanyInfo?: BitrixCompanyModel;
};

export type ApplicationFilterUpdateType = Omit<
  ApplicationFilterModel,
  | "fromLocation"
  | "waypointId"
  | "toLocation"
  | "fromDate"
  | "toDate"
  | "transportCharacteristic"
  | "currency"
  | "borderCrossingLocation"
  | "customsPostsLocation"
> & {
  fromId?: number;
  waypointId?: number;
  toId?: number;
  date?: string;
  arrivalDateTo?: string;
  transportCharacteristicId: number;
  currencyId?: number;
  borderCrossingId?: number;
  customsPostId?: number;
};

export type ServiceCostApplicationCalculationUpdateType = Omit<ServiceCostApplicationCalculationDto, "id" | "bitrixCompanyInfo"> & {
  id?: number;
};

export type ApplicationCalculationUpdateType = Omit<ApplicationCalculationDto, "id" | "serviceCostCalculations" | "calculationSegments"> & {
  id?: number;
  serviceCostCalculations: Array<ServiceCostApplicationCalculationUpdateType>;
};

export type ApplicationDangerousCargoInfoUpdateType = Omit<ApplicationDangerousCargoInfoModel, "documents"> & {
  documents?: Array<DealDocumentModel>;
};

type ApplicationCargoInfoUpdateType = Omit<ApplicationCargoInfoModel, "dangerousCargoInfo" | "cargoDimensionsDocuments"> & {
  cargoDimensionsDocuments?: Array<DealDocumentModel>;
  dangerousCargoInfo?: ApplicationDangerousCargoInfoUpdateType;
};

export type ApplicationUpdateType = Omit<
  ApplicationModel,
  | "type"
  | "creationDate"
  | "indicativeCreationDate"
  | "applicationForOfferCreationDate"
  | "offerCreationDate"
  | "lastDateOfChangeToOffer"
  | "creator"
  | "calculations"
  | "cargoInfo"
  | "documents"
  | "commentInfos"
> & {
  calculations: Array<ApplicationCalculationUpdateType>;
  cargoInfo?: ApplicationCargoInfoUpdateType;
  documents?: Array<DealDocumentModel>;
  commentInfos: Array<ApplicationCommentInfoDto>;
};

export type ApplicationMatchModel = Omit<ApplicationMatchDto, "application"> & {
  application: ApplicationModel;
};
export class ApplicationDataConverter {
  static convertCalculationService(service: ServiceCostApplicationCalculationDto): ServiceCostApplicationCalculationModel {
    return {
      ...service,
      bitrixCompanyInfo: service.bitrixCompanyInfo ? BitrixCompanyDataConverter.convertContractor(service.bitrixCompanyInfo) : undefined,
    };
  }

  static convertCalculationServices(services: Array<ServiceCostApplicationCalculationDto>): Array<ServiceCostApplicationCalculationModel> {
    return services.map((x) => this.convertCalculationService(x));
  }

  static convertCalculationSegment(segment: ApplicationCalculationSegmentDto): ApplicationCalculationSegmentModel {
    return {
      ...segment,
      recommendedRates: RateDataConverter.convertRates(segment.recommendedRates),
      calculationVehicles: segment.calculationVehicles.map((x) => ({
        ...x,
        wishElementList: x.wishElementList.map((w) => ({
          ...w,
          rate: w.rate ? RateDataConverter.convertRate(w.rate) : undefined,
          bitrixCompanyInfo: BitrixCompanyDataConverter.convertContractor(w.bitrixCompanyInfo),
        })),
      })),
    };
  }

  static convertCalculationSegments(segments: Array<ApplicationCalculationSegmentDto>): Array<ApplicationCalculationSegmentModel> {
    return segments.map((x) => this.convertCalculationSegment(x));
  }

  static convertCalculation(calculation: ApplicationCalculationDto): ApplicationCalculationModel {
    return {
      ...calculation,
      isRecommendationPageOpen: calculation.isRecommendationPageOpen ?? false,
      serviceCostCalculations: this.convertCalculationServices(calculation.serviceCostCalculations || []),
      calculationSegments: calculation.calculationSegments ? this.convertCalculationSegments(calculation.calculationSegments) : undefined,
    };
  }

  static convertCalculations(calculations: Array<ApplicationCalculationDto>): Array<ApplicationCalculationModel> {
    return calculations.map((x) => this.convertCalculation(x));
  }

  static convertApplication(
    application: ApplicationDto,
    transportCharacteristic: TransportCharacteristicModel,
    host: "WEB" | "BITRIX",
    currency?: CurrencyModel,
  ): ApplicationModel {
    const {
      filter,
      creationDate,
      indicativeCreationDate,
      applicationForOfferCreationDate,
      offerCreationDate,
      lastDateOfChangeToOffer,
      documents,
      calculations,
      cargoInfo,
      bitrixCompanyInfo,
    } = application;

    return {
      ...application,
      type: transportCharacteristic.loadType,
      creationDate: new Date(creationDate),
      indicativeCreationDate: indicativeCreationDate ? new Date(indicativeCreationDate) : undefined,
      applicationForOfferCreationDate: applicationForOfferCreationDate ? new Date(applicationForOfferCreationDate) : undefined,
      offerCreationDate: offerCreationDate ? new Date(offerCreationDate) : undefined,
      lastDateOfChangeToOffer: lastDateOfChangeToOffer ? new Date(lastDateOfChangeToOffer) : undefined,
      filter: {
        ...filter,
        fromDate: filter.date ? new Date(filter.date) : undefined,
        toDate: filter.arrivalDateTo ? new Date(filter.arrivalDateTo) : undefined,
        transportCharacteristic,
        currency,
        fromLocation: filter.from,
        toLocation: filter.to,
        borderCrossingLocation: filter.borderCrossing,
        customsPostsLocation: filter.customsPost,
        waypointLocation: filter.waypoint,
      },
      documents: documents ? AttachDocumentDataConverter.convertDocumentsToFormData(documents, host) : [],
      calculations: this.convertCalculations(calculations),

      cargoInfo: cargoInfo
        ? {
            ...cargoInfo,
            dangerousCargoInfo: cargoInfo.dangerousCargoInfo
              ? {
                  ...cargoInfo.dangerousCargoInfo,
                  documents: cargoInfo.dangerousCargoInfo.documents
                    ? AttachDocumentDataConverter.convertDocumentsToFormData(cargoInfo.dangerousCargoInfo.documents, host)
                    : undefined,
                }
              : undefined,
            cargoDimensionsDocuments: cargoInfo.cargoDimensionsDocuments
              ? AttachDocumentDataConverter.convertDocumentsToFormData(cargoInfo.cargoDimensionsDocuments, host)
              : undefined,
            cargoCharacteristics: cargoInfo.cargoCharacteristics ? cargoInfo.cargoCharacteristics.sort((a, b) => a.id - b.id) : undefined,
          }
        : undefined,

      commentInfos: application.commentInfos.map((x) => ({ ...x, date: dayjs(x.date) })),
      bitrixCompanyInfo: bitrixCompanyInfo ? BitrixCompanyDataConverter.convertContractor(bitrixCompanyInfo) : undefined,
    };
  }

  static convertApplications(
    applications: Array<ApplicationDto>,
    transportCharacteristics: Array<TransportCharacteristicModel>,
    currencies: Array<CurrencyModel>,
    host: "WEB" | "BITRIX",
  ): Array<ApplicationModel> {
    return applications.map((x) => {
      const transportCharacteristic = transportCharacteristics.find((t) => t.id === x.filter.transportCharacteristicId)!;
      const currency = currencies.find((t) => t.id === x.filter.currencyId)!;
      return this.convertApplication(x, transportCharacteristic, host, currency);
    });
  }

  static convertApplicationLite(application: ApplicationLiteDto): ApplicationLiteModel {
    return {
      ...application,
      creationDate: dayjs(application.creationDate),
      updateDate: application.updateDate ? dayjs(application.updateDate) : undefined,
      bitrixCompanyInfo: application.bitrixCompanyInfo
        ? BitrixCompanyDataConverter.convertContractor(application.bitrixCompanyInfo)
        : undefined,
    };
  }

  static convertApplicationsLite(applications: Array<ApplicationLiteDto>): Array<ApplicationLiteModel> {
    return applications.map((x) => this.convertApplicationLite(x));
  }

  static converFilterForUpdate(filter: ApplicationFilterModel): ApplicationFilterUpdateType {
    const {
      fromLocation,
      waypointLocation,
      toLocation,
      fromDate,
      toDate,
      transportCharacteristic,
      currency,
      borderCrossingLocation,
      customsPostsLocation,
    } = filter;

    return {
      ...filter,
      fromId: fromLocation?.id,
      waypointId: waypointLocation?.id,
      toId: toLocation?.id,
      date: fromDate ? dayjs(fromDate).format("YYYY-MM-DD") : undefined,
      arrivalDateTo: toDate ? dayjs(toDate).format("YYYY-MM-DD") : undefined,
      transportCharacteristicId: transportCharacteristic.id,
      currencyId: currency?.id,
      borderCrossingId: borderCrossingLocation?.id,
      customsPostId: customsPostsLocation?.id,
    };
  }

  static convertCalculationServiceForUpdate(service: ServiceCostApplicationCalculationModel): ServiceCostApplicationCalculationUpdateType {
    return service;
  }

  static convertCalculationServicesForUpdate(
    services: Array<ServiceCostApplicationCalculationModel>,
  ): Array<ServiceCostApplicationCalculationUpdateType> {
    return services.map((x) => this.convertCalculationServiceForUpdate(x));
  }

  static convertCalculationForUpdate(calculation: ApplicationCalculationModel): ApplicationCalculationUpdateType {
    return {
      ...calculation,
      serviceCostCalculations: this.convertCalculationServicesForUpdate(calculation.serviceCostCalculations),
    };
  }

  static convertCalculationsForUpdate(calculations: Array<ApplicationCalculationModel>): Array<ApplicationCalculationUpdateType> {
    return calculations.map((x) => this.convertCalculationForUpdate(x));
  }

  static convertApplicationForUpdate(application: ApplicationModel): ApplicationUpdateType {
    return {
      ...application,
      calculations: this.convertCalculationsForUpdate(application.calculations),
      cargoInfo: application.cargoInfo
        ? {
            ...application.cargoInfo,
            cargoDimensionsDocuments: application.cargoInfo.cargoDimensionsDocuments
              ? AttachDocumentDataConverter.convertDocumentsToUpdate(application.cargoInfo.cargoDimensionsDocuments)
              : undefined,
            dangerousCargoInfo: application.cargoInfo.dangerousCargoInfo
              ? {
                  ...application.cargoInfo.dangerousCargoInfo,
                  documents: application.cargoInfo.dangerousCargoInfo.documents
                    ? AttachDocumentDataConverter.convertDocumentsToUpdate(application.cargoInfo.dangerousCargoInfo.documents)
                    : undefined,
                }
              : undefined,
          }
        : undefined,
      documents: application.documents ? AttachDocumentDataConverter.convertDocumentsToUpdate(application.documents) : [],
      commentInfos: application.commentInfos.map((x) => ({ ...x, date: x.date.format("YYYY-MM-DDTHH:mm:ss.SSSSSSSSS[Z]") })),
    };
  }

  static calculateTotalWeight(cargoCharacteristics: Array<CargoCharacteristicModel | undefined> | undefined): number | undefined {
    if (!cargoCharacteristics) {
      return undefined;
    }

    if (cargoCharacteristics.length === 0) {
      return undefined;
    }

    const weightRows = cargoCharacteristics.filter((x) => x && x.count && x.weight);

    if (weightRows.length === 0) {
      return undefined;
    }

    return +weightRows.reduce((acc, x) => (x && x.count && x.weight ? acc + x.count * x.weight : acc), 0).toFixed(3);
  }

  static calculateTotalVolume(cargoCharacteristics: Array<CargoCharacteristicModel | undefined> | undefined): number | undefined {
    if (!cargoCharacteristics) {
      return undefined;
    }

    if (cargoCharacteristics.length === 0) {
      return undefined;
    }

    const volumeRows = cargoCharacteristics?.filter((x) => x && x.count && x.length && x.height && x.width);

    if (volumeRows.length === 0) {
      return undefined;
    }

    return +volumeRows
      .reduce(
        (acc, x) => (x && x.count && x.length && x.height && x.width ? acc + (x.count * x.height * x.length * x.width) / 1000000 : acc),
        0,
      )
      .toFixed(6);
  }
}

export class ApplicationUtils {
  static getTransportCharacteristicsTypeLabelByType(type: TransportCharacteristicsLoadType, lng: SupportedLanguages): string {
    if (type === "OVERSIZED_CARGO") {
      return i18n.t("oversized_cargo", { lng, ns: "translations" });
    }

    if (type === "RO_RO") {
      return "RORO";
    }

    if (type === "AIR") {
      return i18n.t("avia", { lng, ns: "translations" });
    }

    return type;
  }

  static getApplicationBreadcrumbTitle(application: ApplicationModel, lng: SupportedLanguages) {
    return `${i18n.t("application", { lng })}: ${this.getTransportCharacteristicsTypeLabelByType(
      application.filter.transportCharacteristic.loadType,
      lng,
    )} ${application.number}`;
  }

  static serviceHasVat(service: ServiceCostApplicationCalculationModel) {
    return VatUtils.hasVat(service.vat);
  }

  static servicesHasVat(services: Array<ServiceCostApplicationCalculationModel>) {
    return !!services.find((x) => this.serviceHasVat(x));
  }
}
